import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Save, Plus, X } from "lucide-react";
import { getBrandProfile, putBrandProfile, suggestMission } from "@/lib/weave";

const EMPTY = {
  mission: "",
  mission_action: "",
  mission_skills: "",
  mission_outcome: "",
  one_liner: "",
  strengths: [],
  values: [],
  audience_titles: [],
  audience_needs: [],
  audience_pains: [],
};

function Chips({ items, onChange, placeholder, testId }) {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (!t) return;
    if (!items.includes(t)) onChange([...items, t]);
    setV("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((it, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-full px-3 py-1 text-sm"
            data-testid={`${testId}-chip-${i}`}
          >
            {it}
            <button
              type="button"
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="text-[var(--muted)] hover:text-[var(--red)]"
              aria-label="Remove"
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="rounded-sm border-[var(--line)] h-10"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          onClick={add}
          variant="outline"
          className="rounded-sm h-10 border-[var(--line)]"
        >
          <Plus size={14} />
        </Button>
      </div>
    </div>
  );
}

export default function Foundations() {
  const [p, setP] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  useEffect(() => {
    getBrandProfile()
      .then((data) => setP({ ...EMPTY, ...data }))
      .catch(() => {});
  }, []);

  const update = (key, value) => setP((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await putBrandProfile(p);
      toast.success("Foundations saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const askCoach = async () => {
    if (!p.mission_action || !p.mission_skills || !p.mission_outcome) {
      toast.error("Fill in action, skills and outcome first — even rough drafts.");
      return;
    }
    setThinking(true);
    try {
      const res = await suggestMission({
        action: p.mission_action,
        skills: p.mission_skills,
        outcome: p.mission_outcome,
        extra: p.one_liner || "",
      });
      setAiResult(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach is unavailable.");
    } finally {
      setThinking(false);
    }
  };

  const applySuggestion = (text) => {
    update("mission", text);
    toast.success("Mission updated. Don't forget to save.");
  };

  return (
    <div data-testid="foundations-page" className="space-y-12">
      <div>
        <div className="overline">Module 01</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
          Foundations.
        </h1>
        <p className="mt-3 text-[var(--ink-2)] max-w-2xl">
          A mission, the strengths you bring, the values you won't compromise on, and the audience
          you actually want to reach. Not for show — these become the spine of every post your
          coach helps you write.
        </p>
      </div>

      {/* MISSION */}
      <section className="card-tech rounded-sm p-6 md:p-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-display text-2xl tracking-tight">Mission statement</h2>
          <Button
            type="button"
            onClick={askCoach}
            disabled={thinking}
            className="btn-accent rounded-full h-10 px-4 text-sm flex items-center gap-2"
            data-testid="mission-suggest-btn"
          >
            <Sparkles size={14} /> {thinking ? "Thinking…" : "Ask the coach"}
          </Button>
        </div>
        <p className="text-sm text-[var(--ink-2)]">
          Shape: <span className="font-mono-mark text-xs">"I will [action] by [skills] to [outcome]."</span>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="overline block mb-2">Action</label>
            <Textarea
              rows={3}
              value={p.mission_action}
              onChange={(e) => update("mission_action", e.target.value)}
              placeholder="What you'll do (verbs)"
              className="rounded-sm border-[var(--line)]"
              data-testid="mission-action"
            />
          </div>
          <div>
            <label className="overline block mb-2">Skills</label>
            <Textarea
              rows={3}
              value={p.mission_skills}
              onChange={(e) => update("mission_skills", e.target.value)}
              placeholder="What you bring"
              className="rounded-sm border-[var(--line)]"
              data-testid="mission-skills"
            />
          </div>
          <div>
            <label className="overline block mb-2">Outcome</label>
            <Textarea
              rows={3}
              value={p.mission_outcome}
              onChange={(e) => update("mission_outcome", e.target.value)}
              placeholder="The change you want to see"
              className="rounded-sm border-[var(--line)]"
              data-testid="mission-outcome"
            />
          </div>
        </div>

        <div>
          <label className="overline block mb-2">Your mission (in your words)</label>
          <Textarea
            rows={3}
            value={p.mission}
            onChange={(e) => update("mission", e.target.value)}
            placeholder='I will help early-stage SaaS founders by sharing what I learned scaling Acme to $5M ARR, so they ship faster and stay sane.'
            className="rounded-sm border-[var(--line)] text-base"
            data-testid="mission-final"
          />
        </div>

        {aiResult && (
          <div className="rounded-sm border border-[var(--line)] p-5 bg-[var(--bg-2)]" data-testid="mission-suggestion">
            <div className="overline mb-2" style={{ color: "var(--teal)" }}>Coach suggestion</div>
            <p className="font-display text-xl tracking-tight">{aiResult.mission}</p>
            <Button
              type="button"
              size="sm"
              onClick={() => applySuggestion(aiResult.mission)}
              className="btn-primary rounded-full h-9 px-4 mt-4"
              data-testid="use-mission-btn"
            >
              Use this
            </Button>
            {Array.isArray(aiResult.alternatives) && aiResult.alternatives.length > 0 && (
              <div className="mt-5 space-y-3">
                <div className="overline">Alternatives</div>
                {aiResult.alternatives.map((alt, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 p-3 bg-white rounded-sm border border-[var(--line)]">
                    <div className="text-[var(--ink-2)]">{alt}</div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs h-8"
                      onClick={() => applySuggestion(alt)}
                    >
                      Use
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {aiResult.notes && (
              <p className="mt-5 text-sm text-[var(--ink-2)]">{aiResult.notes}</p>
            )}
          </div>
        )}
      </section>

      {/* STRENGTHS / VALUES */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-tech rounded-sm p-6 space-y-4">
          <div>
            <div className="overline">Module 02 · Strengths</div>
            <h3 className="font-display text-2xl tracking-tight mt-1">What you're great at.</h3>
          </div>
          <Chips
            items={p.strengths}
            onChange={(v) => update("strengths", v)}
            placeholder="e.g. taking complex tech and making it humane"
            testId="strengths"
          />
        </div>
        <div className="card-tech rounded-sm p-6 space-y-4">
          <div>
            <div className="overline">Module 03 · Values</div>
            <h3 className="font-display text-2xl tracking-tight mt-1">What you won't compromise on.</h3>
          </div>
          <Chips
            items={p.values}
            onChange={(v) => update("values", v)}
            placeholder="e.g. honesty over hype"
            testId="values"
          />
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="card-tech rounded-sm p-6 md:p-8 space-y-6">
        <div>
          <div className="overline">Module 04 · Audience</div>
          <h2 className="font-display text-2xl tracking-tight mt-1">
            Who you're writing for.
          </h2>
          <p className="text-sm text-[var(--ink-2)] mt-2">
            The sharper this is, the better your coach gets at suggesting hooks that land.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="overline mb-2">Job titles</div>
            <Chips
              items={p.audience_titles}
              onChange={(v) => update("audience_titles", v)}
              placeholder="e.g. Seed-stage SaaS founder"
              testId="audience-titles"
            />
          </div>
          <div>
            <div className="overline mb-2">Their needs</div>
            <Chips
              items={p.audience_needs}
              onChange={(v) => update("audience_needs", v)}
              placeholder="e.g. shipping faster"
              testId="audience-needs"
            />
          </div>
          <div>
            <div className="overline mb-2">Their pains</div>
            <Chips
              items={p.audience_pains}
              onChange={(v) => update("audience_pains", v)}
              placeholder="e.g. burning out before PMF"
              testId="audience-pains"
            />
          </div>
        </div>
      </section>

      {/* ONE LINER */}
      <section className="card-tech rounded-sm p-6 md:p-8 space-y-3">
        <div>
          <div className="overline">Module 05 · The one-liner</div>
          <h2 className="font-display text-2xl tracking-tight mt-1">
            How you introduce yourself in a room.
          </h2>
        </div>
        <Input
          value={p.one_liner}
          onChange={(e) => update("one_liner", e.target.value)}
          placeholder="I help early-stage SaaS founders ship product without burning out."
          className="rounded-sm border-[var(--line)] h-12 text-base"
          data-testid="one-liner-input"
        />
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="btn-primary rounded-full h-12 px-8 flex items-center gap-2"
          data-testid="foundations-save-btn"
        >
          <Save size={16} /> {saving ? "Saving…" : "Save foundations"}
        </Button>
      </div>
    </div>
  );
}
